import httpClient from '../../../../api/httpClient';

import { 
  Item, 
  ItemListResponse, 
  ItemFilterParams,
  ItemFormData 
} from '../types/item.types';

export const itemApi = {
  /**
   * Get paginated list of items
   */
  getItems: async (params: ItemFilterParams): Promise<ItemListResponse> => {
    const response = await httpClient.get<ItemListResponse>('/items', { params });
    return response.data;
  },

  /**
   * Get a single item by ID
   */
  getItemById: async (id: string): Promise<Item> => {
    const response = await httpClient.get<Item>(`/items/${id}`);
    return response.data;
  },

  /**
   * Create a new item
   */
  createItem: async (data: ItemFormData): Promise<Item> => {
    try {
      console.log('Sending item data:', JSON.stringify(data, null, 2));
      const response = await httpClient.post<Item>('/items', data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        },
        stack: error.stack
      };
      
      console.error('Error creating item:', JSON.stringify(errorDetails, null, 2));
      
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Failed to create item';
      
      const enhancedError = new Error(errorMessage);
      enhancedError.name = error.name || 'ItemCreationError';
      (enhancedError as any).status = error.response?.status;
      (enhancedError as any).validationErrors = error.response?.data?.errors || {};
      
      throw enhancedError;
    }
  },

  /**
   * Update an existing item
   */
  updateItem: async (id: string, data: Partial<ItemFormData>): Promise<Item> => {
    try {
      console.log('Updating item with data:', JSON.stringify(data, null, 2));
      
      // Define read-only fields that should not be included in the update
      const readOnlyFields = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy'];
      
      // Define optional fields that can be set to null
      const optionalFields = ['description', 'costPrice', 'sellingPrice', 'reorderLevel'];
      
      // Create a new object with only defined and allowed fields
      const updateData: Record<string, any> = {};
      
      // Process each field in the form data
      (Object.keys(data) as Array<keyof ItemFormData>).forEach(key => {
        const value = data[key];
        const keyStr = key as string;
        
        // Skip read-only fields
        if (readOnlyFields.includes(keyStr)) {
          return;
        }
        
        // For optional fields, include them even if empty (to clear them)
        if (optionalFields.includes(keyStr)) {
          updateData[keyStr] = value === '' ? null : value;
        }
        // For required fields, only include if they have a value
        else if (value !== undefined && value !== '') {
          updateData[keyStr] = value;
        }
      });
      
      console.log('Processed update data:', JSON.stringify(updateData, null, 2));
      
      const response = await httpClient.patch<Item>(
        `/items/${id}`,
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          validateStatus: (status) => status < 500
        }
      );
      
      if (response.status >= 400) {
        console.error('Update validation error:', response.data);
        const errorMessage = (response.data as any)?.message || 'Update validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        },
        stack: error.stack
      };
      
      console.error('Error updating item:', JSON.stringify(errorDetails, null, 2));
      
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Failed to update item';
      const validationErrors = error.response?.data?.errors || {};
      
      const enhancedError = new Error(errorMessage);
      enhancedError.name = error.name || 'ItemUpdateError';
      (enhancedError as any).status = error.response?.status;
      (enhancedError as any).validationErrors = validationErrors;
      
      throw enhancedError;
    }
  },

  /**
   * Delete an item (soft delete)
   */
  deleteItem: async (id: string): Promise<void> => {
    return httpClient.delete(`/items/${id}`);
  },

  /**
   * Toggle item active status
   */
  toggleItemStatus: async (id: string, isActive: boolean): Promise<Item> => {
    const response = await httpClient.patch<Item>(`/items/${id}/toggle-status`, { isActive });
    return response.data;
  },

  /**
   * Check if item code already exists
   */
  checkItemCodeExists: async (itemCode: string, excludeId?: string): Promise<boolean> => {
    try {
      const response = await httpClient.get<{ exists: boolean }>(
        '/items/check-code',
        { params: { itemCode, excludeId } }
      );
      return response.data.exists;
    } catch (error) {
      console.error('Error checking item code:', error);
      return false;
    }
  },

  /**
   * Bulk import items from CSV (array of objects)
   */
  importItems: async (items: any[]): Promise<void> => {
    const response = await httpClient.post('/items/import', items, {
      headers: {
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status < 500
    });
    if (response.status >= 400) {
      throw new Error(response.data?.message || 'Import failed');
    }
    return response.data;
  },
};

export default itemApi;
